import React, { useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { Tooltip, useTheme } from '@mui/material';
import styles from './Header.styles';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'common/alertContext';
import { useMeQuery } from 'hooks/queries';
import EditButton from '../EditButton/EditButton';
import EditThemeModal from '../EditThemeModal/EditThemeModal';
import EditMentorImpossibleModal from '../EditMentorImpossibleModal/EditMentorImpossibleModal';
import EditMentorModal from '../EditMentorModal/EditMentorModal';
import AvatarGroup from 'ui/components/shared/AvatarGroup/AvatarGroup';
import GoBackButton from 'ui/components/shared/GoBackButton';
import { StatusChip } from 'ui/components/StatusChip/StatusChip';
import { formatSessionDate } from 'utils/sessions';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import Button from 'ui/components/Button/Button';
import FinishProgramModal from '../FinishProgramModal/FinishProgramModal';
import ReopenProgramModal from '../ReopenProgramModal/ReopenProgramModal';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CoachViewModalWithRequest from 'ui/components/shared/coaches/CoachViewModalWithRequest';
import MenteeDetailModalWithRequest from 'ui/components/shared/MenteeDetailModal/MenteeDetailModalWithRequest';
import { roleList } from 'utils/constUtils';
import { parseJsonTranslate } from 'utils/parse';
import ThemeViewModal from 'ui/components/shared/theme/ThemeViewModal';

const Header = ({
  coachee,
  coach,
  coachId,
  companyName,
  expertLabel,
  selectedCoaches,
  startedAt,
  rhEmails = [],
  isRh,
  status,
  statusBeforeDeactivation,
  showEditButton,
  showEndProgram,
  showReopenProgram,
  programTheme,
  topic,
  themeId,
  onChangeTheme,
  onChangeMentor,
  onDeactivateProgram,
  onFinishProgram,
  onReopenProgram,
}) => {
  const { t } = useTranslation();
  const { data: user } = useMeQuery();
  const theme = useTheme();
  const themedStyles = styles(theme);
  const [openThemeModal, setOpenThemeModal] = useState(false);
  const [openEditMentorImpossible, setOpenEditMentorImpossible] = useState(false);
  const [openMentorModal, setOpenMentorModal] = useState(false);
  const [finishProgramModal, setFinishProgramModal] = useState(false);
  const [reopenProgramModal, setReopenProgramModal] = useState(false);

  let avatarConfigCoaches = [];

  if (status === 'pending' || (status === 'deactivated' && statusBeforeDeactivation === 'pending')) {
    avatarConfigCoaches = selectedCoaches.map(({ id, user, picture }) => {
      const { firstname, lastname } = user;
      return { id, picture, firstname, lastname };
    });
  }

  const onClickExpert = () => {
    if (status === 'pending') {
      setOpenEditMentorImpossible(true);
    } else {
      setOpenMentorModal(true);
    }
  };

  const coacheTitle = () => {
    if (user.role === roleList.COACHEE) {
      return t('your_coach', { expertLabel: user?.company?.expertLabel });
    }

    return user?.company?.expertLabel;
  };

  const coacheeTitle = () => {
    if (user.role === roleList.COACH) {
      return t('your_coachee', { clientLabel: user?.company?.clientLabel });
    }

    return user?.company?.clientLabel;
  };

  const isValidCoachee = coachee.firstname && coachee.lastname;

  const [modalOpened, setModalOpened] = useState(false);

  function modalThemeLink(theme) {
      const onClick = (event) => {
        event.stopPropagation();
        theme?.id ? setModalOpened(true) : setModalOpened(false);
      };
      const handleClose = (event) => {
        event.stopPropagation();
        setModalOpened(false);
      };
      /* ********************************** HOOKS ********************************* */
    
      /* ******************************** RENDERING ******************************* */
      return (
        <Typography
         sx={{
          ...themedStyles.topicName,
          cursor: 'pointer',
          }}
          alignItems={'center'}
          direction={'row'}
          data-tag="allowRowEvents"
          style={{ width: '-webkit-fill-available' }}
          onClick={onClick}
          >
          {parseJsonTranslate(theme?.name)}
          {theme?.id && modalOpened && <ThemeViewModal themeId={theme?.id} open={modalOpened} onClose={handleClose} />}
        </Typography>
      );
  }

  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" sx={themedStyles.container}>
      <Box sx={themedStyles.containerGoBack}>
        <GoBackButton />
      </Box>
      <Box sx={themedStyles.programInformation}>

        {modalThemeLink(programTheme)}

        <Box sx={themedStyles.programInformationDetail}>
          <StatusChip expertLabel={expertLabel} status={status} style={themedStyles.statusChip} />
          <Typography sx={themedStyles.startedAt} variant="h6">
            {t('session_launch_date', { sessionDate: formatSessionDate(startedAt) })}
          </Typography>
        </Box>
        {!isRh && !!rhEmails?.length && (
          <Tooltip placement="bottom" title={t('program_header_manager_contact_tooltip', { count: rhEmails.length })}>
            <Box sx={themedStyles.containerRhContact}>
              <MailOutlineIcon sx={{ fontSize: '18px' }} />
              <Typography
                sx={themedStyles.contactManager}
                variant="h6"
                onClick={(e) => {
                  window.location.href = `mailto:${rhEmails.join(';')}`;
                  e.preventDefault();
                }}>
                {t('program_header_manager_contact', { count: rhEmails.length, companyName })}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>
      {!!avatarConfigCoaches.length && (
        <Box sx={themedStyles.containerItemMedium}>
          <Typography sx={themedStyles.title}>
            {t('program_header_proposed_expert', { expertLabel: user?.company?.expertLabel })}
          </Typography>
          <Box sx={themedStyles.containerUser}>
            <AvatarGroup avatarConfig={avatarConfigCoaches} width={40} />
          </Box>
        </Box>
      )}
      {((status !== 'pending' && status !== 'deactivated') ||
        (status === 'deactivated' && statusBeforeDeactivation !== 'pending')) &&
        coach &&
        user.role !== roleList.COACH && (
          <Box sx={themedStyles.containerItemMedium}>
            <UserInformation
              email={coach.user.email}
              picture={coach.picture}
              title={coacheTitle()}
              userName={`${coach.user.firstname} ${coach.user.lastname}`}
              userTitle={coach.title}
              userId={coach.id}
              currentUserRole={user.role}
              userRole={roleList.COACH}
            />
          </Box>
        )}
      {user.role !== roleList.COACHEE && isValidCoachee && (
        <Box sx={themedStyles.containerItemMedium}>
          <UserInformation
            email={coachee.email}
            picture={coachee.picture}
            title={coacheeTitle()}
            userName={`${coachee.firstname} ${coachee.lastname}`}
            userTitle={coachee.title}
            userId={coachee.id}
            user={user}
            currentUserRole={user.role}
            userRole={roleList.COACHEE}
          />
        </Box>
      )}
      {showEditButton && (
        <Box sx={themedStyles.containerActions}>
          <EditButton
            expertLabel={user?.company?.expertLabel}
            clientLabel={user?.company?.clientLabel}
            onClickTheme={() => {
              setOpenThemeModal(true);
            }}
            onClickExpert={onClickExpert}
          />
        </Box>
      )}
      {showEndProgram && (
        <Box sx={themedStyles.containerActions}>
          <Button onClick={() => setFinishProgramModal(true)}>{t('finish_program_button')}</Button>
        </Box>
      )}
      {showReopenProgram && (
        <Box sx={themedStyles.containerActions}>
          <Button onClick={() => setReopenProgramModal(true)}>{t('reopen_program_button')}</Button>
        </Box>
      )}
      {openThemeModal && (
        <EditThemeModal
          company={user?.company}
          open={openThemeModal}
          themeId={themeId}
          onClose={() => {
            setOpenThemeModal(false);
          }}
          onChangeTheme={onChangeTheme}
        />
      )}
      <EditMentorImpossibleModal
        expertLabel={user?.company?.expertLabel}
        clientLabel={user?.company?.clientLabel}
        open={openEditMentorImpossible}
        onDeactivateProgram={onDeactivateProgram}
        onClose={() => {
          setOpenEditMentorImpossible(false);
        }}
      />
      {openMentorModal && (
        <EditMentorModal
          coachee={coachee}
          expertLabel={user?.company?.expertLabel}
          mentorIdSelected={coachId}
          open={openMentorModal}
          onChangeMentor={onChangeMentor}
          onClose={() => {
            setOpenMentorModal(false);
          }}
        />
      )}
      <FinishProgramModal
        programName={topic}
        open={finishProgramModal}
        onClose={() => {
          setFinishProgramModal(false);
        }}
        onFinishProgram={onFinishProgram}
      />
      <ReopenProgramModal
        programName={topic}
        open={reopenProgramModal}
        onClose={() => {
          setReopenProgramModal(false);
        }}
        onReopenProgram={onReopenProgram}
      />
    </Stack>
  );
};

const UserInformation = ({ user, userId, title, picture, userName, userTitle, email, currentUserRole, userRole }) => {
  const { t } = useTranslation();
  const { setStateAlert } = useAlert();
  const theme = useTheme();
  const themedStyles = styles(theme);
  const [showCoachViewInModal, setShowCoachViewInModal] = useState(false);
  const [showCoacheeViewInModal, setShowCoacheeViewInModal] = useState(false);

  const onClick = (event, field) => {
    event.stopPropagation();
    navigator.clipboard.writeText(field);
    setStateAlert({ open: true, message: t('email_copied'), type: 'info' });
  };

  const onClickModal = () => {
    if (userRole === roleList.COACH) {
      setShowCoachViewInModal(true);
    } else {
      setShowCoacheeViewInModal(true);
    }
  };

  return (
    <>
      <Typography sx={themedStyles.title}>{title}</Typography>
      <Box sx={themedStyles.containerUser}>
        <Box sx={themedStyles.containerUserClickable} onClick={onClickModal}>
          <Avatar sx={themedStyles.avatar} src={picture} />
          <Box sx={themedStyles.containerUserInformations}>
            <Box sx={themedStyles.userInformation}>
              <Typography sx={themedStyles.userName}>{userName}</Typography>
              <EmailOutlinedIcon sx={themedStyles.mailIcon} onClick={(e) => onClick(e, email)} />
            </Box>
            {userTitle && <Typography sx={themedStyles.userTitle}>{userTitle}</Typography>}
          </Box>
        </Box>
      </Box>

      {showCoachViewInModal && (
        <CoachViewModalWithRequest
          user={user}
          userRole={currentUserRole}
          coachId={userId}
          open={showCoachViewInModal}
          onClose={() => {
            setShowCoachViewInModal(false);
          }}
        />
      )}

      {showCoacheeViewInModal && (
        <MenteeDetailModalWithRequest
          userId={userId}
          open={showCoacheeViewInModal}
          onClose={() => {
            setShowCoacheeViewInModal(false);
          }}
        />
      )}
    </>
  );
};

export default Header;
